<script setup>
  import MobileNav from '@/Components/Navs/MobileNav.vue';
  import { ref, onMounted, computed, onBeforeUnmount } from 'vue';
  import { usePage } from '@inertiajs/vue3';
  import { Head, Link, router } from '@inertiajs/vue3';
  import DropdownComponent from '@/Components/DropdownComponent.vue';
  import DropdownItem from '@/Components/DropdownItem.vue';
  import NavbarToggleButton from '@/Components/Navs/NavbarToggleButton.vue';
  import DropdownLink from '@/Components/DropdownLink.vue';
  import LogoSVG from '@/Components/LogoSVG.vue';
  import TrialStatus from '@/Components/TrialStatus.vue';

  import OverlayBadge from 'primevue/overlaybadge';
  import axios from 'axios';
  import { useToast } from 'primevue/usetoast';
  import SearchBar from '../SearchBar.vue';
  import Logo from '@/Components/Logo.vue';
  import { useNotificationStore } from '@/Stores/notifications';
  import { usePushNotificationStore } from '@/Stores/pushNotifications';
  import { WebSocketService } from '@/services/websocket';
  import SecondaryButton from '@/Components/SecondaryButton.vue';
  import LogoSVGWhite from '../LogoSVGWhite.vue';
  import { menuItems } from '@/constants/menuItems';

  const toast = useToast();
  const showMobileNav = ref(false);
  const toggleNavbar = () => {
    showMobileNav.value = !showMobileNav.value;
  };

  const closeOnClickOutside = e => {
    if (showMobileNav.value && !e.target.closest('#navbarSupportedContent')) {
      showMobileNav.value = false;
    }
  };

  const page = usePage();
  const notificationStore = useNotificationStore();
  const pushNotificationStore = usePushNotificationStore();
  let webSocketService = null;

  const user = computed(() => page.props.auth.user);

  const logOut = async () => {
    await axios.post('/logout');
    router.push('/login');
  };

  const closeNav = () => {
    showMobileNav.value = false;
  };

  const unreadNotificationsCount = computed(
    () => notificationStore.unreadCount
  );

  const unreadMessagesCount = ref(0);

  const getunreadmessages = async () => {
    return false;
    axios
      .get('/api/messenger/unread-threads-count')
      .then(response => {
        unreadMessagesCount.value = response.data.unread_threads_count;
        console.log('Unread messages count:', unreadMessagesCount);
      })
      .catch(error => {
        console.error('Error fetching unread messages count:', error);
      });
  };

  const handleNotificationClick = () => {
    pushNotificationStore.requestBrowserNotification();
  };

  onMounted(() => {
    // Debug: Check if trial data is available
    if (page.props.trialData?.hasActiveTrial) {
      console.log('SgNavbar - User has active trial:', page.props.trialData.remainingDays, 'days left');
    }

    document.addEventListener('click', closeOnClickOutside);

    if (page.props.auth.user?.id) {
      webSocketService = new WebSocketService(
        page.props.auth.user.id,
        notificationStore
      );
      webSocketService.initialize();
    }

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').then(() => {
        console.log('Service worker registered');
      });
    }

    notificationStore.fetchNotifications();
  });

  onMounted(() => {
    document.addEventListener('click', closeOnClickOutside);
    Echo.private('App.Models.User.' + page.props.auth.user.id).notification(
      notification => {
        if (notification.type != 'App\\Notifications\\NewGigmessageInstant') {
          toast.add({
            severity: 'success',
            summary: 'Notification',
            detail: notification.message,
            life: 2000
          });
          notificationStore.fetchNotifications();
        }
      }
    );
  });
  onBeforeUnmount(() => {
    document.removeEventListener('click', closeOnClickOutside);

    // Clean up WebSocket connections
    if (webSocketService) {
      webSocketService.disconnect();
    }
  });

  const handleToastClick = notification => {
    console.log('Toast clicked', notification);
  };
</script>

<template>
  <Toast />
  <nav class="w-full bg-white dark:!bg-gray-950">
    <div
      class="mx-auto flex max-w-6xl flex-row items-center justify-between bg-transparent py-1"
    >
      <div class="w-1/4 items-center md:ml-10">
        <div class="mx-auto">
          <a
            :href="route('home')"
            class="!text-black hover:!text-black dark:!text-white dark:hover:!text-white"
          >
            <!-- <div class="dark:hidden">
             <LogoSVG />
           </div>
           <div class="hidden mt-1 dark:block">
             <LogoSVGWhite />
           </div> -->
            <LogoSVG class="dark:!text-white" />
          </a>
        </div>
      </div>

      <div class="flex w-1/3 flex-row items-center justify-end pt-1">
        <div class="mr-2 md:mr-6">
          <DropdownComponent
            top="20px"
            right="-40px"
            @click="handleNotificationClick"
            @touchend="handleNotificationClick"
          >
            <template v-slot:button>
              <div class="mt-2">
                <template v-if="unreadNotificationsCount">
                  <OverlayBadge
                    :value="unreadNotificationsCount"
                    severity="danger"
                    size="small"
                  >
                    <a href="#">
                      <i
                        class="fa fa-bell text-gray-900 dark:text-gray-200"
                      ></i>
                    </a>
                  </OverlayBadge>
                </template>
                <template v-else>
                  <a href="#">
                    <i class="fa fa-bell text-gray-900"></i>
                  </a>
                </template>
              </div>
            </template>
            <template v-slot:content>
              <div class="relative flex flex-col overflow-y-auto p-2 text-sm">
                <p class="h-10 text-center">
                  <DropdownLink
                    :href="route('notifications')"
                    class="rounded-md !text-white transition duration-200 ease-in-out hover:!text-white"
                  >
                    <span class="text-white hover:underline">
                      Notifications
                    </span>
                  </DropdownLink>
                </p>
                <ul class="flex flex-col">
                  <li
                    v-for="notification in notificationStore.notifications"
                    :key="notification.id"
                    class="hover!text-white !text-white hover:underline"
                    style="color: white !important"
                  >
                    <div class="mt-2">
                      <Link
                        :href="
                          notification.data.url ??
                          `/notification/${notification.id}`
                        "
                        v-tooltip.top="notification.data.message"
                        style="z-index: 900; color: white !important"
                        @click="
                          notificationStore.toggleReadStatus(notification)
                        "
                      >
                        {{
                          notification.data.message.length > 40
                            ? notification.data.message.substring(0, 40) + '...'
                            : notification.data.message
                        }}
                      </Link>
                    </div>
                  </li>
                  <li class="mx-auto mt-2">
                    <DropdownLink
                      :href="route('notifications')"
                      class="hover:!text-underline mx-auto text-center !text-white hover:!text-white"
                    >
                      <span class="text-white hover:text-white hover:underline">
                        View All Notifications
                      </span>
                    </DropdownLink>
                  </li>
                </ul>
              </div>
            </template>
          </DropdownComponent>
        </div>
        <ul class="hidden flex-row items-center gap-4 md:mr-16 md:flex">
          <li>
            <TrialStatus 
              :user="user" 
              :trial-data="page.props.trialData || {}"
            />
          </li>
          <div
            :href="route('home')"
            v-if="$page.props.jetstream.managesProfilePhotos"
            class="flex rounded-full border-2 border-primary text-sm transition focus:border-purple-700 focus:outline-none"
          >
            <Link :href="route('home')">
              <img
                class="h-8 w-8 rounded-full object-cover"
                :src="$page.props.auth?.user.profile_photo_url"
                :alt="$page.props.auth?.user.name"
              />
            </Link>
          </div>
        </ul>

        <NavbarToggleButton
          @toggle-navbar="toggleNavbar"
          textColor="text-primary"
        />
      </div>
      <MobileNav
        v-show="showMobileNav"
        @close-mobile-nav="closeNav"
        :menu-items="menuItems"
        :user="user"
      />
    </div>
  </nav>
</template>

<style scoped>
  .p-tooltip {
    width: 300px;
    background-color: rgba(240, 232, 232, 0.8);
    color: white;
    border-radius: 4px;
    padding: 8px;
    font-size: 12px;
    text-align: center;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
    z-index: 99999999999;
  }

  .p-tooltip .p-tooltip-arrow {
    border-color: rgba(0, 0, 0, 0.8) transparent transparent transparent;
  }

  .mobile-nav-button {
    @apply p-4;
  }

  .slide-enter-active,
  .slide-leave-active {
    transition: transform 0.3s ease-in-out;
  }

  .slide-enter-from {
    transform: translateX(100%);
  }

  .slide-enter-to {
    transform: translateX(0);
  }

  .slide-leave-from {
    transform: translateX(0);
  }

  .slide-leave-to {
    transform: translateX(100%);
  }
</style>
